"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Loader2, RefreshCw, ZoomIn } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { useBillableActions } from "@/app/context/BillableActionsContext"
import { ImageModal } from "@/app/components/ui/ImageModal"
import { GenerationLoadingComponent } from "@/app/components/GenerationLoadingComponent"
import { BillableActionInfo } from "@/app/components/BillableActionInfo"

interface GeneratedDesignsProps {
  designs: string[]
  selectedDesign: string | null
  onSelect: (design: string) => void
  isGenerating: boolean
  isRegenerating?: boolean
  onRegenerate?: (newDesigns: string[]) => void
  onComplete?: () => void
  onBack?: () => void
  imageSet?: "default" | "canvas" | "scratch" // New prop to specify which image set to use
  isCreatingProject?: boolean
  estimatedDuration?: number
  landscape?: boolean | null // Added prop for regeneration cost calculation
}

// Default design images for regular wizard
const DEFAULT_DESIGN_IMAGES = [
  "/images/generated-designs/design-1-kitchen.png",
  "/images/generated-designs/design-2-kitchen.png",
  "/images/generated-designs/design-3-kitchen.png",
  "/images/generated-designs/design-4-kitchen.png",
  "/images/generated-designs/design-5-kitchen.png",
  "/images/generated-designs/design-6-kitchen.png",
  "/images/generated-designs/design-7-kitchen.png",
  "/images/generated-designs/design-8-kitchen.png",
  "/images/generated-designs/design-9-kitchen.png",
]

// Canvas design images for canvas wizard
const CANVAS_DESIGN_IMAGES = [
  "/images/canvas-designs/farmhouse-kitchen-1.png",
  "/images/canvas-designs/farmhouse-kitchen-2.png",
  "/images/canvas-designs/farmhouse-kitchen-3.png",
  "/images/canvas-designs/farmhouse-kitchen-4.png",
  "/images/canvas-designs/farmhouse-kitchen-5.png",
  "/images/canvas-designs/farmhouse-kitchen-6.png",
  "/images/canvas-designs/farmhouse-kitchen-7.png",
  "/images/canvas-designs/farmhouse-kitchen-8.png",
  "/images/canvas-designs/farmhouse-kitchen-9.png",
]

// Default design descriptions
const DEFAULT_DESIGN_DESCRIPTIONS = [
  "Modern Farmhouse Kitchen",
  "Vaulted Ceiling Kitchen",
  "Rustic Brick Kitchen",
  "Cozy Breakfast Nook Kitchen",
  "Mediterranean Style Kitchen",
  "Traditional Brick Floor Kitchen",
  "Farmhouse Open Shelving Kitchen",
  "Elegant Shiplap Kitchen",
  "Modern Brick Backsplash Kitchen",
]

// Canvas design descriptions
const CANVAS_DESIGN_DESCRIPTIONS = [
  "Sage Green Farmhouse Kitchen",
  "Fresh Coffee Kitchen Design",
  "Modern Farmhouse with Open Shelving",
  "Clean White Farmhouse Kitchen",
  "Rustic Wood Island Kitchen",
  "Vintage Farmhouse Style",
  "Open Shelving Cottage Kitchen",
  "Classic Farmhouse Design",
  "Cream & Wood Farmhouse Kitchen",
]

export function GeneratedDesigns({
  designs,
  selectedDesign,
  onSelect,
  isGenerating,
  isRegenerating = false,
  onRegenerate,
  onComplete,
  onBack,
  imageSet = "default",
  isCreatingProject = false,
  estimatedDuration,
  landscape = null, // Added parameter
}: GeneratedDesignsProps) {
  const [selectedImageForModal, setSelectedImageForModal] = useState<string | null>(null)
  const [selectedDesignIndex, setSelectedDesignIndex] = useState<number>(0)
  const [regenerationCount, setRegenerationCount] = useState(0)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [selectedRadioDesign, setSelectedRadioDesign] = useState<string | null>(null)
  const { getActionCost } = useBillableActions()

  const REAL_DESIGN_IMAGES = imageSet === "canvas" ? CANVAS_DESIGN_IMAGES : DEFAULT_DESIGN_IMAGES
  const DESIGN_DESCRIPTIONS = imageSet === "canvas" ? CANVAS_DESIGN_DESCRIPTIONS : DEFAULT_DESIGN_DESCRIPTIONS

  useEffect(() => {
    setRegenerationCount(0)
    setFeedbackSubmitted(false)
  }, [])

  const handleRegenerate = () => {
    if (designs.length >= 9) return

    console.log("[v0] GeneratedDesigns handleRegenerate called")
    console.log("[v0] Scrolling to top...")
    window.scrollTo({ top: 0, behavior: "instant" })

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "instant" })
      console.log("[v0] Second scroll attempt completed")
    }, 100)

    const newCount = regenerationCount + 1
    setRegenerationCount(newCount)

    if (newCount >= 3 && !feedbackSubmitted) {
      setShowFeedbackModal(true)
      return
    }

    if (onRegenerate) {
      onRegenerate([])
    }
  }

  const handleRadioSelect = (designIndex: number) => {
    const actualImageUrl = designs[designIndex]
    setSelectedRadioDesign(actualImageUrl)
    onSelect(actualImageUrl)
  }

  const handleImageClick = (imageUrl: string) => {
    const index = designs.indexOf(imageUrl)
    setSelectedImageForModal(imageUrl)
    setSelectedDesignIndex(index)
  }

  const closeModal = () => {
    setSelectedImageForModal(null)
  }

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && selectedImageForModal) {
        closeModal()
      }
    }

    if (selectedImageForModal) {
      document.addEventListener("keydown", handleEscKey)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey)
      document.body.style.overflow = "unset"
    }
  }, [selectedImageForModal])

  const handleSubmitFeedback = () => {
    setFeedbackSubmitted(true)
    setShowFeedbackModal(false)
    setFeedback("")

    setTimeout(() => {
      const remainingSlots = 9 - designs.length
      const newDesignCount = Math.min(3, remainingSlots)
      const startIndex = designs.length
      const newDesigns = REAL_DESIGN_IMAGES.slice(startIndex, startIndex + newDesignCount)

      if (onRegenerate) {
        onRegenerate(newDesigns)
      }
    }, 1000)
  }

  const handleSkipFeedback = () => {
    setFeedbackSubmitted(true)
    setShowFeedbackModal(false)

    setTimeout(() => {
      const remainingSlots = 9 - designs.length
      const newDesignCount = Math.min(3, remainingSlots)
      const startIndex = designs.length
      const newDesigns = REAL_DESIGN_IMAGES.slice(startIndex, startIndex + newDesignCount)

      if (onRegenerate) {
        onRegenerate(newDesigns)
      }
    }, 1000)
  }

  const handleCreateProject = () => {
    if (onComplete) {
      onComplete()
    }
  }

  const regenerationCost = getActionCost(
    imageSet === "canvas"
      ? landscape
        ? "GENERATE_CONCEPTS_FROM_CANVAS_LANDSCAPE_REGEN"
        : "GENERATE_CONCEPTS_FROM_CANVAS_REGEN"
      : imageSet === "scratch"
        ? "GENERATE_CONCEPTS_FROM_SCRATCH_REGEN"
        : "GENERATE_CONCEPTS_FROM_SCRATCH",
  )

  console.log("[v0] GeneratedDesigns imageSet:", imageSet)
  console.log("[v0] GeneratedDesigns landscape:", landscape)
  console.log(
    "[v0] GeneratedDesigns action being used:",
    imageSet === "canvas"
      ? landscape
        ? "GENERATE_CONCEPTS_FROM_CANVAS_LANDSCAPE_REGEN"
        : "GENERATE_CONCEPTS_FROM_CANVAS_REGEN"
      : imageSet === "scratch"
        ? "GENERATE_CONCEPTS_FROM_SCRATCH_REGEN"
        : "GENERATE_CONCEPTS_FROM_SCRATCH",
  )
  console.log("[v0] GeneratedDesigns regenerationCost:", regenerationCost)

  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-12 w-12 text-[#40A1AB] animate-spin mb-4" />
        <h2 className="text-xl font-semibold mb-2">Generating Your Designs</h2>
        <p className="text-gray-600 text-center max-w-md">
          Our AI is creating beautiful interior designs based on your preferences. This may take a moment...
        </p>
      </div>
    )
  }

  const buttonText = "See More Designs"

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Your Generated Designs</h2>
        <div className="flex flex-col items-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            disabled={isRegenerating || designs.length >= 9}
            title={designs.length >= 9 ? "Maximum of 9 designs reached" : "Generate 3 more designs"}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRegenerating ? "animate-spin" : ""}`} />
            {buttonText} {regenerationCost > 0 && `(${regenerationCost} credits)`}
          </Button>
          <BillableActionInfo className="text-sm text-right" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {designs.map((design, index) => {
          const actualImageUrl = designs[index]
          const isSelected = selectedRadioDesign === actualImageUrl

          return (
            <div key={index} className="relative border rounded-lg overflow-hidden">
              <div className="relative aspect-video cursor-pointer group" onClick={() => handleImageClick(design)}>
                <Image
                  src={design || "/placeholder.svg"}
                  alt={`Generated design ${index + 1}`}
                  fill
                  className="object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              <div className="p-3 bg-white flex items-center justify-between">
                <p className="font-medium">Design {index + 1}</p>
                <input
                  type="radio"
                  name="selectedDesign"
                  checked={isSelected}
                  onChange={() => handleRadioSelect(index)}
                  className="w-4 h-4 text-[#40A1AB] border-gray-300 focus:ring-[#40A1AB]"
                />
              </div>
            </div>
          )
        })}
      </div>

      {isRegenerating && (
        <GenerationLoadingComponent
          message="Generating new designs..."
          showText={true}
          estimatedDuration={estimatedDuration}
        />
      )}

      {designs.length > 0 && (
        <div className="text-center text-sm text-gray-500">{designs.length} of 9 designs generated</div>
      )}

      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          Choose your favorite design to create a project, or revisit any design later in <strong>My Concepts</strong>.
          All generated designs are automatically saved to your Dashboard. You may generate up to 9 designs per project.
        </p>
      </div>

      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={onBack} disabled={!onBack || isRegenerating}>
          Back
        </Button>

        <Button
          className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
          onClick={handleCreateProject}
          disabled={!selectedRadioDesign || isCreatingProject}
        >
          {isCreatingProject ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Project...
            </>
          ) : (
            "Create Project"
          )}
        </Button>
      </div>

      <ImageModal
        isOpen={!!selectedImageForModal}
        onClose={closeModal}
        imageSrc={selectedImageForModal}
        caption={`Design ${selectedDesignIndex + 1}`}
        showDownload={false}
      />

      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="sticky top-0 bg-white z-10 p-6 pb-2">
            <DialogTitle>Help Us Improve</DialogTitle>
            <DialogDescription>
              We noticed you've regenerated designs multiple times. Could you share what you're looking for to help us
              improve?
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 pt-2">
            <Textarea
              placeholder="What specific design elements are you looking for? What would make these designs better?"
              className="min-h-[120px] mb-4"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />

            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleSkipFeedback} className="sm:mr-2 bg-transparent">
                Skip
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                disabled={!feedback.trim()}
              >
                Submit Feedback
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
